import { ArrayUtil, DateUtil, Model, ModelRow, StringUtil } from "@mcleod/core";
import {
    Checkbox, DataSourceValidationEvent, ExcludableTextbox, LayoutProvidedTaskDecoratorProps, ReportData,
    ReportPreviewTabSource
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { getDispatchControlBoolean } from "@mcleod/general";
import { AutogenLayoutTransferToCommissions } from "./autogen/AutogenLayoutTransferToCommissions";

const TASK_DESCRIPTION = "Transfer to Commissions";

export class TransferToCommissions extends AutogenLayoutTransferToCommissions {
    private _containerEnabled = false;
    private payrollControl: ModelRow;

    public override get taskDecoratorProps(): Partial<LayoutProvidedTaskDecoratorProps> {
        const afterExecution = (tabSource: ReportPreviewTabSource[]) => {
            let noRecordsTransferred = ArrayUtil.isEmptyArray(tabSource);
            if (!noRecordsTransferred) {
                const firstItem = ArrayUtil.getFirstElement(tabSource);
                noRecordsTransferred = tabSource?.length === 1 &&
                    firstItem != null &&
                    firstItem instanceof ReportData &&
                    firstItem.reportName.endsWith("Exceptions");
            }
            if (noRecordsTransferred) {
                CommonDialogs.showError("No records to transfer", "Transfer Error");
            }
        };
        return {
            processLogDescription: TASK_DESCRIPTION,
            doAfterSearch: afterExecution,
            doAfterPost: afterExecution,
            executeButtonCaption: "Transfer",
            confirmationDialogTitle: "Transfer Confirmation",
            confirmationDialogMessage: "Are you sure you would like to proceed?"
        };
    }

    override async onLoad(): Promise<void> {
        this.textboxTransferBy.selectedItem = this.textboxTransferBy.items[0];
        this.textboxDrayMove.selectedItem = this.textboxDrayMove.items[0];

        this._containerEnabled = getDispatchControlBoolean("is_container_active", false);
        this.textboxDrayMove.visible = this._containerEnabled;

        this.findComponentsByType(Checkbox).forEach(cb => cb.visible = false);

        this.excludabletextboxOrderMove.switchRange.addChangeListener(() => {
            this.clearExcludeableRangeFields(this.excludabletextboxOrderMove);
        });
        this.excludabletextboxManifest.switchRange.addChangeListener(() => {
            this.clearExcludeableRangeFields(this.excludabletextboxManifest);
        });
        this.payrollControl = await Model.searchSingleRecord("lme/dispatch/payroll-control", null, ["enable_commissions"]);
        this.daterangefilterFilter.textboxStart.text = "m";
        this.daterangefilterFilter.textboxEndDate.text = "me";
    }

    private clearExcludeableRangeFields(tb: ExcludableTextbox) {
        this.mainDataSource.searchRow.set(tb.textboxRangeStart.field, null);
        this.mainDataSource.searchRow.set(tb.textboxRangeEnd.field, null);
    }

    textboxTransferByOnChange() {
        const dateType = this.textboxTransferBy.text;
        const isDelivery = dateType.startsWith("Delivery");
        const isOrder = dateType.startsWith("Order");
        const isManifest = dateType.startsWith("Manifest");

        if (isDelivery) {
            this.excludabletextboxOrderMove.visible = false;
            this.excludabletextboxManifest.visible = false;
            this.daterangefilterFilter.visible = true;
        } else if (isOrder) {
            this.excludabletextboxOrderMove.switchRange.checked = false;
            this.excludabletextboxOrderMove.visible = true;

            this.excludabletextboxManifest.visible = false;
            this.daterangefilterFilter.visible = false;

        } else if (isManifest) {
            this.excludabletextboxManifest.switchRange.checked = false;
            this.excludabletextboxManifest.visible = true;

            this.excludabletextboxOrderMove.visible = false;
            this.daterangefilterFilter.visible = false;
        }
    }

    /** This is an event handler for the onValidate event of sourceTransferToSettlementMultiple. */
    sourceTransferToCommissionsOnValidate(event: DataSourceValidationEvent) {
        if (this.payrollControl.getBoolean("enable_commissions", false) === false) {
            event.validationResults.push({
                isValid: false,
                component: this.textboxTransferBy,
                caption: "Commissions is not enabled on Settlement Control."
            });
            return;
        }
        const startDate = this.daterangefilterFilter.textboxStart.getDataValue();
        const endDate = this.daterangefilterFilter.textboxEndDate.getDataValue();
        const datesAreFilled = !StringUtil.isEmptyString(startDate) && !StringUtil.isEmptyString(endDate);
        if (datesAreFilled && this.textboxTransferBy?.selectedItem?.value === "2") {
            if (DateUtil.parseDateWithKeywords(startDate) > DateUtil.parseDateWithKeywords(endDate)) {
                event.validationResults.push({
                    isValid: false,
                    component: this.daterangefilterFilter.textboxStart,
                    caption: "Start date must be before end date."
                });
            }
        }
        if (this.textboxTransferBy?.selectedItem?.value === "0") {
            const startValue = this.excludabletextboxOrderMove.textboxRangeStart?.getDataValue();
            const endValue = this.excludabletextboxOrderMove.textboxRangeEnd?.getDataValue();
            if (!StringUtil.isEmptyString(startValue) && !StringUtil.isEmptyString(endValue) && startValue > endValue) {
                event.validationResults.push({
                    isValid: false,
                    component: this.excludabletextboxOrderMove,
                    caption: "Start value must be before end value."
                });
            }
        }
        if (this.textboxTransferBy?.selectedItem?.value === "1") {
            const startValue = this.excludabletextboxManifest.textboxRangeStart?.getDataValue();
            const endValue = this.excludabletextboxManifest.textboxRangeEnd?.getDataValue();
            if (!StringUtil.isEmptyString(startValue) && !StringUtil.isEmptyString(endValue) && startValue > endValue) {
                event.validationResults.push({
                    isValid: false,
                    component: this.excludabletextboxManifest,
                    caption: "Start value must be before end value."
                });
            }
        }
    }
}
